
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Card, CardContent, Typography, TextField, Button, Checkbox, FormControlLabel, Alert, CircularProgress, InputAdornment } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { getApiUrl } from '../config/api';

function SignUp() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    mobileNumber: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    terms: false,
    promo: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const [referralValidation, setReferralValidation] = useState({ status: '', message: '' });
  const [validatingCode, setValidatingCode] = useState(false);
  const navigate = useNavigate();

  const handleChange = e => {
    const { name, value, type, checked } = e.target;
    setForm(f => ({ ...f, [name]: type === 'checkbox' ? checked : value }));
    
    // Reset referral validation when code changes
    if (name === 'referralCode') {
      setReferralValidation({ status: '', message: '' });
    }
  };

  // Validate referral code
  const validateReferralCode = async (code) => {
    if (!code || code.trim() === '') {
      setReferralValidation({ status: '', message: '' });
      return;
    }

    setValidatingCode(true);
    try {
      const res = await fetch(getApiUrl('/api/affiliates/validate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code: code.trim() })
      });
      const data = await res.json();
      
      if (data.success && data.valid) {
        setReferralValidation({
          status: 'valid',
          message: 'Valid referral code'
        });
      } else {
        setReferralValidation({
          status: 'invalid',
          message: data.message || 'Invalid or inactive referral code'
        });
      }
    } catch (err) {
      setReferralValidation({
        status: 'error',
        message: 'Failed to validate code'
      });
    } finally {
      setValidatingCode(false);
    }
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (!form.terms) {
      setError('You must accept the Terms and Conditions.');
      return;
    }
    if (!form.firstName || !form.email || !form.password || form.password !== form.confirmPassword) {
      setError('Please fill all required fields and ensure passwords match.');
      return;
    }
    if (!form.mobileNumber || form.mobileNumber.trim() === '') {
      setError('Mobile number is required.');
      return;
    }
    setError('');
    try {
      const payload = {
        firstName: form.firstName,
        lastName: form.lastName,
        email: form.email,
        mobileNumber: form.mobileNumber.trim(),
        password: form.password
      };

      // Include referral code if provided and valid
      if (form.referralCode && form.referralCode.trim() !== '') {
        payload.referralCode = form.referralCode.trim();
      }

      const res = await fetch(getApiUrl('/api/register'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json();
      if (res.ok && data.success) {
        setSubmitted(true);
        // Success message will remain until user navigates manually
      } else {
        setError(data.error || 'Registration failed.');
      }
    } catch (err) {
      setError('Registration failed. Please try again.');
    }
  };

  if (submitted) {
    return (
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
        <Card sx={{ minWidth: 340, maxWidth: 500, mx: 2, boxShadow: 3 }}>
          <CardContent>
            <Typography variant="h5" color="success.main" mb={2} align="center">
              Registration Successful!
            </Typography>
            <Typography variant="body1" align="center" sx={{ color: '#64748b', mb: 2 }}>
              Your account has been created successfully. You can now sign in with your email and password to access the platform.
            </Typography>
            <Button 
              variant="contained" 
              fullWidth
              onClick={() => navigate('/signin')}
              sx={{ 
                mt: 2, 
                fontWeight: 600, 
                backgroundColor: '#4ade80 !important', 
                color: '#fff !important',
                '&:hover': { 
                  backgroundColor: '#22c55e !important',
                  color: '#fff !important'
                }
              }}
            >
              Go to Sign In
            </Button>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'background.default' }}>
      <Card sx={{ minWidth: 340, maxWidth: 400, mx: 2, boxShadow: 3 }}>
        <CardContent>
          <Typography variant="h5" fontWeight={700} align="center" mb={2} color="primary">Sign Up</Typography>
          <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField name="firstName" label="First Name" value={form.firstName} onChange={handleChange} required fullWidth />
            <TextField name="lastName" label="Last Name" value={form.lastName} onChange={handleChange} required fullWidth />
            <TextField name="email" label="Email Address" type="email" value={form.email} onChange={handleChange} required fullWidth />
            
            {/* Mobile Number Field with Country Code Support */}
            <TextField 
              name="mobileNumber" 
              label="Mobile Number (with country code)" 
              value={form.mobileNumber} 
              onChange={handleChange} 
              required 
              fullWidth
              placeholder="e.g., +1 234-567-8900 or +91 98765 43210"
              helperText="Include country code (e.g., +1, +44, +91)"
            />
            
            <TextField name="password" label="Password" type="password" value={form.password} onChange={handleChange} required fullWidth />
            <TextField name="confirmPassword" label="Confirm Password" type="password" value={form.confirmPassword} onChange={handleChange} required fullWidth />
            
            {/* Referral Code Field */}
            <Box>
              <TextField 
                name="referralCode" 
                label="Referral Code (Optional)" 
                value={form.referralCode} 
                onChange={handleChange}
                onBlur={(e) => validateReferralCode(e.target.value)}
                fullWidth
                placeholder="Enter referral code if you have one"
                InputProps={{
                  endAdornment: validatingCode ? (
                    <InputAdornment position="end">
                      <CircularProgress size={20} />
                    </InputAdornment>
                  ) : referralValidation.status === 'valid' ? (
                    <InputAdornment position="end">
                      <CheckCircleIcon sx={{ color: '#4ade80' }} />
                    </InputAdornment>
                  ) : referralValidation.status === 'invalid' ? (
                    <InputAdornment position="end">
                      <CancelIcon sx={{ color: '#ef4444' }} />
                    </InputAdornment>
                  ) : null
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&.Mui-focused fieldset': {
                      borderColor: referralValidation.status === 'valid' ? '#4ade80' : 
                                   referralValidation.status === 'invalid' ? '#ef4444' : undefined
                    }
                  }
                }}
              />
              {referralValidation.status && (
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: referralValidation.status === 'valid' ? '#4ade80' : '#ef4444',
                    mt: 0.5,
                    display: 'block'
                  }}
                >
                  {referralValidation.message}
                </Typography>
              )}
            </Box>

            <FormControlLabel
              control={<Checkbox name="terms" checked={form.terms} onChange={handleChange} color="primary" />}
              label={<span><span style={{ color: '#fff', fontWeight: 'bold', fontSize: '1.1em', marginRight: 4 }}>*</span>Accept Terms and Conditions</span>}
            />
            <FormControlLabel
              control={<Checkbox name="promo" checked={form.promo} onChange={handleChange} color="secondary" />}
              label={<Typography variant="body2">Receive promotional offers</Typography>}
            />
            {error && <Alert severity="error">{error}</Alert>}
            <Button variant="contained" type="submit" size="large" sx={{ mt: 1, fontWeight: 600, backgroundColor: '#4ade80 !important', background: '#4ade80 !important', color: '#1e293b', '&:hover': { backgroundColor: '#22c55e !important', background: '#22c55e !important', color: '#1e293b' } }}>
              Submit
            </Button>
            <Box sx={{ textAlign: 'center', mt: 1 }}>
              <Typography variant="body2" sx={{ color: '#64748b' }}>
                Already have an account?{' '}
                <Typography 
                  component="span" 
                  sx={{ 
                    color: '#4ade80', 
                    fontWeight: 600, 
                    cursor: 'pointer',
                    '&:hover': { 
                      color: '#22c55e',
                      textDecoration: 'underline'
                    }
                  }}
                  onClick={() => navigate('/signin')}
                >
                  Sign In
                </Typography>
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default SignUp;
